import { usePagination, useActions, Page } from "@nosto/search-js/preact/hooks";
import { JSX } from "preact/jsx-runtime";
import style from "./Pagination.module.css";
import { getPageUrl } from "@/mapping/url/getPageUrl";
import { cl } from "@nosto/search-js/utils";

type Props = {
  ariaLabel?: string;
} & JSX.IntrinsicElements["a"];

function PageLink({ onClick, href, ariaLabel, children }: Props) {
  return (
    <a href={href} aria-label={ariaLabel} onClick={onClick}>
      {children}
    </a>
  );
}

export default function Pagination() {
  const { prev, first, pages, last, next } = usePagination({
    width: 3,
  });

  const { updateSearch } = useActions();

  function pageLinkProps({ from, page }: Page) {
    return {
      href: getPageUrl(page),
      onClick: (e: Event) => {
        e.preventDefault();
        updateSearch({ products: { from } });
        scrollTo(0, 0);
      },
    };
  }

  if (pages.length === 1) {
    return <></>;
  }

  // Initialize a flat array for all our list items
  const paginationItems: JSX.Element[] = [];

  if (prev) {
    paginationItems.push(
      <li key="prev" className="previous-page">
        <PageLink {...pageLinkProps(prev)} ariaLabel="Previous page">
          Prev
        </PageLink>
      </li>,
    );
  }

  if (first) {
    paginationItems.push(
      <li key="first-link">
        <PageLink {...pageLinkProps(first)} ariaLabel="First page">
          {first.page}
        </PageLink>
      </li>,
      <li key="first-filler">
        <span className={style.filler}>...</span>
      </li>,
    );
  }

  pages.forEach((page) => {
    paginationItems.push(
      <li key={`page-${page.page}`} className={cl(page.current && "selected")}>
        <PageLink ariaLabel={`${page.page} page`} {...pageLinkProps(page)}>
          {page.page}
        </PageLink>
      </li>,
    );
  });

  if (last) {
    paginationItems.push(
      <li key="last-filler">
        <span className={style.filler}>...</span>
      </li>,
      <li key="last-link">
        <PageLink {...pageLinkProps(last)} ariaLabel="Last page">
          {last.page}
        </PageLink>
      </li>,
    );
  }

  if (next) {
    paginationItems.push(
      <li key="next" className="next-page">
        <PageLink {...pageLinkProps(next)} ariaLabel="Next page">
          Next
        </PageLink>
      </li>,
    );
  }

  return <ul className="plp__pagination-list">{paginationItems}</ul>;
}
